import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';

import { Form } from 'react-final-form';
import Modal from 'react-modal';
import { NotificationManager } from 'react-notifications';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { FORM_ERROR } from 'final-form';
import moment from 'moment';

import '../assets/styles/loan/LoanPayment.scss';
import { renderBnplLayout } from '../components/Bnpl/BnplLayout';
import { Breadcrumb, BreadcrumbItem } from '../components/Breadcrumb';
import FormDatePicker from '../components/FormElements/FormDatePicker';
import FormMoneyInput from '../components/FormElements/FormMoneyInput';
import FormSelect from '../components/FormElements/FormSelect';
import Layout from '../components/common/Layout';
import TenantConfig from '../config';
import configData from '../config';

/** context */
import { UserContext } from '../context/UserContext';
import { formatUSDCurrency } from '../helpers/format';
import { composeValidators, required, requireAmount, minAmount } from '../helpers/validation';
import {
  getLoanPaymentMethods,
  sendPayment,
  getOneTimePaymentInfo,
  getScheduledOneTimePayment,
  removeScheduledOneTimePayment,
  sendPaymentWithInstrument,
  getPaymentInstrumentList,
} from '../service/loan';

Modal.setAppElement('#root');

const LoanPayment = () => {
  const navigate = useNavigate();
  const params = useParams();
  const loan = params.id;

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentInstrument, setPaymentInstrument] = useState([]);
  const [oneTimePaymentInfo, setOneTimePaymentInfo] = useState(null);
  const [scheduledOneTimePayment, setScheduledOneTimePayment] = useState(null);
  const [allowedDates, setAllowedDates] = useState([]);
  const [selectedDateAndAmount, setSelectedDateAndAmount] = useState(null);
  const [maxAmountWarningText, setMaxAmountWarningText] = useState('');
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const { setLoading } = useContext(UserContext);

  const isBnpl = configData.getTenant() === 'lightrx';
  const conditionForShowBnplDropdown =
    paymentInstrument.filter((el) => el.value.maskedPaymentIdentifier).length > 0;
  const selectOptions = useMemo(
    () =>
      paymentMethods.map((pm) => ({
        title: pm.accountNumber,
        value: pm.accountReferenceNo,
      })),
    [paymentMethods]
  );
  const optionalCondition = isBnpl ? conditionForShowBnplDropdown : selectOptions.length !== 0;

  useEffect(() => {
    const fetchPaymentInstruments = async () => {
      try {
        setLoading(true);
        const { data } = await getPaymentInstrumentList(params.id);
        setPaymentInstrument(data.filter((el) => el.dataType === 'tokenizedPayment'));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const { data } = await getLoanPaymentMethods(loan);
        setPaymentMethods(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const fetchOneTimePaymentInfo = async () => {
      try {
        setLoading(true);
        const { data } = await getOneTimePaymentInfo(loan);
        if (data.allowedDatesAndAmounts.length > 0) {
          if (!isBnpl) {
            const allowedDates = data.allowedDatesAndAmounts.map((item) => {
              return moment.tz(item.date, 'America/Los_Angeles').toDate();
            });
            setAllowedDates(allowedDates);
          } else {
            const selectedDateAndAmount = data.allowedDatesAndAmounts[0];
            setSelectedDateAndAmount(selectedDateAndAmount);
          }
          setOneTimePaymentInfo(data);
        }
        setLoading(false);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const fetchScheduledOneTimePayment = async () => {
      try {
        setLoading(true);
        const { data } = await getScheduledOneTimePayment(loan);
        if (!isBnpl && data && data.length > 0) {
          setScheduledOneTimePayment(data[0]);
        } else {
          fetchOneTimePaymentInfo();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    scrollToTop();
    fetchPaymentMethods();
    fetchScheduledOneTimePayment();
    fetchPaymentInstruments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan, isUpdated]);

  const onSubmitInternal = useCallback(
    async ({ paymentAmount, accountReferenceNo }) => {
      const { accountNumber, routingNumber } = paymentMethods.find(
        (pm) => pm.accountReferenceNo === parseInt(accountReferenceNo)
      );

      try {
        setLoading(true);
        await sendPayment(loan, {
          paymentAmount: paymentAmount.replace('$', '').replaceAll(',', ''),
          paymentDate: selectedDateAndAmount.date,
          accountReferenceNo: parseInt(accountReferenceNo, 10),
          routingNumber,
          accountNumber,
        });

        NotificationManager.success(
          `A payment amount of ${paymentAmount} has been scheduled for ${moment(
            selectedDateAndAmount.date
          ).format('MM-DD-YYYY')}`,
          'Success'
        );
        setLoading(false);
        navigate(`/account/${loan}`);
      } catch (err) {
        let message = 'Payment failed. Please verify all the fields and try again.';

        NotificationManager.warning(message, 'Warning');
        return { [FORM_ERROR]: message };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, loan, paymentMethods, selectedDateAndAmount]
  );

  const onSubmitInternalByInstrument = async ({ paymentAmount, accountReferenceNo }) => {
    const findedInstrument = paymentInstrument.find(
      ({ value }) => value.token === accountReferenceNo
    );
    try {
      setLoading(true);
      await sendPaymentWithInstrument(loan, {
        paymentAmount: paymentAmount.replace('$', '').replaceAll(',', ''),
        paymentNotes: moment(selectedDateAndAmount.date).format('MM-DD-YYYY'),
        financialInstrumentId: findedInstrument.instrumentId,
      });
    } catch (e) {
      console.log(e);
    } finally {
      navigate(`/account/${loan}`);
      setLoading(false);
    }
  };

  const correctSubmittingFn = isBnpl ? onSubmitInternalByInstrument : onSubmitInternal;

  const selectOptionsForBnpl = useMemo(
    () =>
      paymentInstrument
        .filter((el) => el.value.maskedPaymentIdentifier && el.value.token !== 'test-token')
        .map(({ value }) => ({
          title: 'Account Ending In ' + value.maskedPaymentIdentifier,
          value: value.token,
        })),
    [paymentInstrument]
  );

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const handleDateSelect = (date) => {
    const selectedDateAndAmount = oneTimePaymentInfo.allowedDatesAndAmounts.find(
      (item) => new Date(item.date).getDate() === new Date(date).getDate()
    );
    setSelectedDateAndAmount(selectedDateAndAmount);
  };

  const checkMaximumAmount = (values) => {
    const { value } = values;
    if (selectedDateAndAmount && selectedDateAndAmount.amount < value) {
      setMaxAmountWarningText('The payment amount entered is above the current balance');
    } else {
      setMaxAmountWarningText('');
    }
  };
  const navigationFn = () => {
    localStorage.setItem('prevPath', location.pathname);
    navigate(`/account/${loan}/managePayment/addCard`);
  };

  const addPaymentMethod = (absolutePosition = false) => {
    if (TenantConfig.dashboard.showPaymentMethod) {
      return (
        <div className={`addPaymentMethod ${absolutePosition ? 'absolute' : ''}`}>
          <span>
            <a onClick={navigationFn} href="">
              {' '}
              Add a payment method
            </a>
          </span>
        </div>
      );
    }
    return null;
  };

  const deleteScheduledOneTimePayment = async () => {
    try {
      setLoading(true);
      await removeScheduledOneTimePayment(scheduledOneTimePayment.loanId, scheduledOneTimePayment);
      NotificationManager.success(
        'The selected scheduled one time payment has been removed',
        'Success',
        2000
      );
      setOpenConfirmModal(false);
      setScheduledOneTimePayment(null);
      setIsUpdated(true);
    } catch (e) {
      console.error(e);
    }
  };

  return renderBnplLayout(
    <Layout>
      <div className="section loanPayment">
        <Breadcrumb>
          <BreadcrumbItem className="breadcrumbItem">
            <Link to={`/dashboard`}>Dashboard</Link>
          </BreadcrumbItem>
          <BreadcrumbItem className="breadcrumbItem">
            <Link to={`/account/${loan}`}>{`Loan #${loan}`}</Link>
          </BreadcrumbItem>
          <BreadcrumbItem className="breadcrumbItem">Payment</BreadcrumbItem>
        </Breadcrumb>

        <h1 className="title">
          Loan #{loan}
          <br />
          {scheduledOneTimePayment ? 'Details of your scheduled payment' : 'Make a Payment'}
        </h1>
        {scheduledOneTimePayment ? (
          <>
            <div className="table">
              <div className="row tableRow">
                <div className="detailLabel">Scheduled date</div>
                <div className="detailValue">
                  {moment(scheduledOneTimePayment.scheduleDate).format('YYYY-MM-DD')}
                </div>
              </div>
              <div className="row tableRow">
                <div className="detailLabel">Amount</div>
                <div className="detailValue">
                  {formatUSDCurrency(scheduledOneTimePayment.totalAmount)}
                </div>
              </div>
              <div className="row tableRow">
                <div className="detailLabel">Payment Method</div>
                <div className="detailValue">{scheduledOneTimePayment.accountNumber}</div>
              </div>
            </div>
            <div className="actions">
              <a onClick={() => navigate(-1)}>Go Back</a>
              <button
                type="button"
                className="btn primary-btn"
                onClick={() => setOpenConfirmModal(true)}
              >
                Delete
              </button>
            </div>
          </>
        ) : (
          <Form
            onSubmit={correctSubmittingFn}
            render={({ handleSubmit, form, submitting, pristine, submitError, valid }) => (
              <form onSubmit={handleSubmit} className="form">
                {!isBnpl && (
                  <FormDatePicker
                    label="Select a date"
                    name="paymentDate"
                    dateFormat="yyyy-MM-dd"
                    required
                    handleDateSelect={handleDateSelect}
                    includeDates={allowedDates}
                  />
                )}
                <FormMoneyInput
                  label={`Enter amount - Min: ${
                    oneTimePaymentInfo
                      ? formatUSDCurrency(oneTimePaymentInfo.minPaymentAmount, 0)
                      : formatUSDCurrency(0, 0)
                  }, Max: ${
                    selectedDateAndAmount ? formatUSDCurrency(selectedDateAndAmount.amount) : '-'
                  } (Payoff amount)`}
                  name="paymentAmount"
                  required
                  validate={composeValidators(
                    requireAmount('You enter the amount'),
                    minAmount(
                      `The minimum amount is ${
                        oneTimePaymentInfo ? oneTimePaymentInfo.minPaymentAmount : 0
                      }`,
                      oneTimePaymentInfo ? oneTimePaymentInfo.minPaymentAmount : 0
                    )
                  )}
                  handleValueChange={checkMaximumAmount}
                  maxAmountWarningText={maxAmountWarningText}
                />
                {optionalCondition && (
                  <div style={{ position: 'relative' }}>
                    {addPaymentMethod(true)}
                    <FormSelect
                      label="Payment Method"
                      required
                      name="accountReferenceNo"
                      placeholder="Pick the payment method"
                      options={isBnpl ? selectOptionsForBnpl : selectOptions}
                      validate={required('Payment method should be selected')}
                    />
                  </div>
                )}
                {!optionalCondition && addPaymentMethod()}
                <div className="actions">
                  <a onClick={() => navigate(-1)}>Go Back</a>
                  <button
                    disabled={submitting || pristine || !valid || !!maxAmountWarningText}
                    type="submit"
                    className="paymentButton btn primary-btn"
                  >
                    Make the Payment
                  </button>
                </div>
                {submitError && <p className="error-text">{submitError}</p>}
              </form>
            )}
          />
        )}
      </div>
      <Modal isOpen={openConfirmModal} className="field-modal">
        <div>
          <span onClick={() => setOpenConfirmModal(false)}>x</span>
          <div className="modal-header">
            <h3 className="text-center">Are you sure you want to delete this scheduled payment?</h3>
          </div>
          <div className="modal-content">
            <div className="d-flex justify-content-center">
              <button type="button" onClick={deleteScheduledOneTimePayment}>
                Yes
              </button>
              <button type="button" onClick={() => setOpenConfirmModal(false)}>
                No
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default LoanPayment;
